/**
 * @fileoverview The note under the What to plant list (D67, owner 27 Oct): where the list comes from (the plants on the
 * gardener's Plants list) and how to add more (a photo of seed packets, a forwarded seed order email). The server says
 * every word and which pieces are links; this draws each line's pieces in order, a piece with an `href` as a link: an app
 * path through the router, a mailto: as a plain link.
 *
 * Where: WhatToPlantPage, below the groups (also when there are no groups).
 * Words (en/es): none of its own; all are the server's.
 *
 * @module WhatToPlantMore
 */

import React from 'react';
import { Link } from 'react-router-dom';
import type { WhatToPlantMore as More, WhatToPlantPiece } from '@/shared/api/whatToPlant';

/** What the note is handed. */
interface WhatToPlantMoreProps {
  /** The server's note. */
  more: More;
}

/** The classes of a link in the note. */
const LINK = 'font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * One piece of a line: plain words, a router link for an app path, or a plain anchor for a mailto:.
 *
 * @param piece - The piece.
 * @returns The piece.
 */
function Piece({ piece }: { piece: WhatToPlantPiece }): JSX.Element {
  if (!piece.href) return <>{piece.text}</>;
  if (piece.href.startsWith('/')) return <Link to={piece.href} className={LINK}>{piece.text}</Link>;
  return <a href={piece.href} className={LINK}>{piece.text}</a>;
}

/**
 * The note: one paragraph per line, each line its pieces in order.
 *
 * @returns The note.
 */
export const WhatToPlantMore: React.FC<WhatToPlantMoreProps> = ({ more }) => (
  <div className="mt-6 space-y-1 text-sm text-muted-foreground">
    {more.lines.map((line, i) => (
      <p key={i}>{line.map((piece, j) => <Piece key={j} piece={piece} />)}</p>
    ))}
  </div>
);
